import pointerHelper from '@Common/Helpers/PointerHelper';
import { GridColumn } from './GridColumn';
import { GridRender } from './GridRender';
import { Grid } from './SimpleGridLight';

interface IColumnLimits {
    max: number;
    min: number;
}

class GridResizerUnit {
    public leftColumn: GridColumn;
    public rightColumn: GridColumn;
    public unit: JQuery;
    public gridResizer: GridResizer;

    public reposition(height?: number): void {
        const rightColumn = this.rightColumn.tableColumn();
        const mid = rightColumn.position().left + parseInt(rightColumn.css('border-left-width'), 10) / 2;
        const unitWidth = this.unit.width();
        const unitLeft = mid - Math.ceil((unitWidth === 0 ? 10 : unitWidth) / 2);
        this.unit.css('left', unitLeft);
        if (typeof height !== 'undefined') {
            this.unit.css('height', height);
        }
    }

    private static getColumnLimits(col: GridColumn): IColumnLimits {
        return {
            max: Number.MAX_VALUE,
            min: col.minWidth
        };
    }

    private static correctColumnLimits(limit1: IColumnLimits, limit2: IColumnLimits, common: number): IColumnLimits {
        return {
            max: Math.min(common - limit2.min, limit1.max),
            min: Math.max(common - limit2.max, limit1.min)
        };
    }

    private static keepLimits(limit: IColumnLimits, newValue: number): number {
        return Math.max(limit.min, Math.min(limit.max, newValue));
    }

    public init(): void {
        const left = this.leftColumn;
        const right = this.rightColumn;
        let leftWidth;
        let rightWidth;
        let delta;
        let startDrag;
        let leftLimits: IColumnLimits;
        let rightLimits: IColumnLimits;
        this.unit.draggable({
            axis: 'x',
            containment: 'parent',
            start: event => {
                this.gridResizer.startDrag(this.unit.css('cursor'));
                startDrag = pointerHelper.getPosition(event, null).x;
                leftWidth = left.width();
                rightWidth = right.width();
                const commonWidth = leftWidth + rightWidth;
                leftLimits = GridResizerUnit.getColumnLimits(left);
                rightLimits = GridResizerUnit.getColumnLimits(right);
                leftLimits = GridResizerUnit.correctColumnLimits(leftLimits, rightLimits, commonWidth);
                rightLimits = GridResizerUnit.correctColumnLimits(rightLimits, leftLimits, commonWidth);
            },
            drag: event => {
                delta = startDrag - pointerHelper.getPosition(event, null).x;
                const newLeftWidth = GridResizerUnit.keepLimits(leftLimits, leftWidth - delta);
                const newRightWidth = GridResizerUnit.keepLimits(rightLimits, rightWidth + delta);
                left.setUserWidth(newLeftWidth);
                right.setUserWidth(newRightWidth);
            },
            stop: () => {
                this.gridResizer.stopDrag();
            }
        });
    }
}

export class GridResizer {
    public _grid: Grid;
    public _context: GridRender;
    public _resizer: JQuery;
    private _units: GridResizerUnit[];
    private rootCursor: string;

    constructor(grid: Grid, context: GridRender) {
        this._grid = grid;
        this._context = context;
    }

    public init(): void {
        this._resizer = $('<div>', {
            class: 'simpleGrid-columnResizer',
            css: {
                position: 'absolute'
            }
        });
        this._units = this.buildUnits();
    }

    private buildUnits(): GridResizerUnit[] {
        const columns = this._grid.columns();
        let prevColumn: GridColumn = null;
        const units: GridResizerUnit[] = [];
        for (const column of columns) {
            if (prevColumn != null) {
                const unit = new GridResizerUnit();
                unit.leftColumn = prevColumn;
                unit.rightColumn = column;
                unit.unit = $('<span>', {
                    class: 'simpleGrid-columnResizerUnit',
                    css: {}
                }).appendTo(this._resizer);
                unit.gridResizer = this;
                unit.init();
                units.push(unit);
            }
            prevColumn = column;
        }

        return units;
    }

    private reposition(): void {
        const offset = this._context.headerTable.position();
        const outerWidth = this._context.headerTable.outerWidth();
        const outerHeight = this._context.headerTable.outerHeight();
        this._resizer.css({
            top: offset.top,
            left: offset.left,
            width: outerWidth
        });

        for (const unit of this._units) {
            unit.reposition(outerHeight);
        }
    }

    public render(): void {
        this.reposition();
        this._resizer.appendTo(this._context.headerTable.parent());
    }

    public startDrag(cursor: string): void {
        this.rootCursor = this._context.root.css('cursor');
        this._context.root.css('cursor', cursor);
    }

    public stopDrag(): void {
        this._context.root.css('cursor', this.rootCursor);
        this.reposition();
    }
}
